import type { AdminGame, SgdbIdentifyResponse, SgdbSearchResult } from '@mcgames/contracts';
import { Button, Dialog, Spinner, Toggle } from '@mcgames/ui';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorText } from './ErrorText.tsx';
import { useRememberSgdbGame, useSgdbGame, useSgdbIdentify } from './game-hooks.ts';
import styles from './Games.module.css';
import { IMAGE_KINDS, imageForKind } from './media-kinds.ts';
import { SgdbSearch } from './SgdbSearch.tsx';

export type SgdbIdentifyProps = {
  game: AdminGame;
  sgdbEnabled: boolean;
};

/**
 * Identifies the game on SteamGridDB: the admin picks the game once and the server imports the
 * best cover, hero, logo and icon. Images the game already has stay unless the admin asks to
 * replace them. Each image can still be changed one by one below.
 */
export function SgdbIdentify({ game, sgdbEnabled }: SgdbIdentifyProps) {
  const { t } = useTranslation('admin');
  const headingId = useId();
  const [open, setOpen] = useState(false);
  const [replace, setReplace] = useState(false);
  const [outcomes, setOutcomes] = useState<SgdbIdentifyResponse['outcomes'] | null>(null);
  const identify = useSgdbIdentify(game.id);
  const rememberSgdbGame = useRememberSgdbGame();
  const hasImages = IMAGE_KINDS.some((kind) => imageForKind(game, kind) !== null);
  const isNew = game.sgdbId === null && !hasImages;

  function pick(result: SgdbSearchResult) {
    identify.mutate(
      { sgdbId: result.id, replaceExisting: hasImages && replace },
      {
        onSuccess: (response) => {
          rememberSgdbGame(result);
          setOutcomes(response.outcomes);
          setOpen(false);
        },
      },
    );
  }

  function openSearch() {
    identify.reset();
    setOpen(true);
  }

  return (
    <section
      className={`${styles['identify']} ${isNew && sgdbEnabled ? styles['identifyNew'] : ''}`}
      aria-labelledby={headingId}
    >
      <h3 id={headingId} className={styles['subheading']}>
        {t('games.identify.heading')}
      </h3>
      {!sgdbEnabled ? (
        <p className={styles['muted']}>{t('games.identify.disabled')}</p>
      ) : (
        <>
          {game.sgdbId === null ? (
            <p className={styles['muted']}>{t('games.identify.prompt')}</p>
          ) : (
            <IdentifiedAs sgdbId={game.sgdbId} />
          )}
          <div className={styles['row']}>
            <Button
              variant={game.sgdbId === null ? 'primary' : 'secondary'}
              disabled={identify.isPending}
              onClick={openSearch}
            >
              {t(game.sgdbId === null ? 'games.identify.start' : 'games.identify.again')}
            </Button>
            {hasImages && (
              <Toggle
                checked={replace}
                onChange={setReplace}
                label={t('games.identify.replace')}
                disabled={identify.isPending}
              />
            )}
          </div>
          {identify.isPending && !open && <Progress />}
          {/* Inside the dialog the search shows the error; once it is closed, it shows here. */}
          {!open && <ErrorText error={identify.error} />}
          {outcomes !== null && (
            <ul className={styles['outcomes']} aria-label={t('games.identify.summary')}>
              {IMAGE_KINDS.map((kind) => (
                <li key={kind} className={styles[`outcome-${outcomes[kind]}`]}>
                  {t('games.identify.summaryItem', {
                    kind: t(`games.kinds.${kind}`),
                    outcome: t(`games.identify.outcome.${outcomes[kind]}`),
                  })}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      {open && (
        <Dialog
          open
          onClose={() => setOpen(false)}
          title={t('games.identify.heading')}
          footer={
            <Button variant="secondary" onClick={() => setOpen(false)}>
              {t('actions.close', { ns: 'common' })}
            </Button>
          }
        >
          <SgdbSearch
            initialQuery={game.title}
            busy={identify.isPending}
            error={identify.error}
            onPick={pick}
          />
          {identify.isPending && <Progress />}
        </Dialog>
      )}
    </section>
  );
}

function IdentifiedAs({ sgdbId }: { sgdbId: number }) {
  const { t } = useTranslation('admin');
  const sgdbGame = useSgdbGame(sgdbId);
  const name = sgdbGame.data?.name;
  return (
    <p className={styles['identified']}>
      {name !== undefined
        ? t('games.identify.identified', { name })
        : t('games.identify.identifiedId', { id: sgdbId })}
    </p>
  );
}

function Progress() {
  const { t } = useTranslation('admin');
  return (
    <p className={`${styles['row']} ${styles['hint']}`}>
      <Spinner size={16} />
      <span>{t('games.identify.progress')}</span>
    </p>
  );
}
